// Locale entries are validated by isValidLocale before use.
export type FtlManifest = Record<string, Record<string, unknown>>;

// Mirrors PREVIEW_INSTITUTION in indigo/settings/utils.py. Previews share one translations
// bucket, each under its workspace name, which common/settings/preview.py uses as the key prefix.
const PREVIEW_INSTITUTION = /^preview_([a-z0-9](?:[a-z0-9-]*[a-z0-9])?)$/i;

const getIsDev = (internalName: string) => /_local$/i.test(internalName);

// common/settings/preview.py refuses these, since is_local, is_uat and is_production match on them.
const RESERVED_SUFFIXES = /(?:local|uat|prod)$/i;

export const getTranslationsKeyPrefix = (internalName: string): string => {
  const workspace = PREVIEW_INSTITUTION.exec(internalName)?.[1];
  return workspace && !RESERVED_SUFFIXES.test(workspace) ? `${workspace.toLowerCase()}/` : "";
};

const DEFAULT_TIMEOUT = 15000;
// Total attempts = 1 + MAX_RETRIES. Translation files are static per release,
// so a retry is a cheap, safe way to recover transient failures (network drops,
// S3/CDN blips, a cold default-ftl endpoint).
//
// We deliberately retry EVERY failure type, including deterministic-looking
// 4xx. The current Sentry capture records no status or URL, so we can't yet
// tell which failures are transient, and S3/CloudFront 403/404s can themselves
// be transient during deploy/propagation windows. Retrying is harmless for
// genuinely persistent failures — they just fall through after the final
// attempt. Once the enriched capture (see AppLocalizationProvider) gives us
// status codes, revisit whether specific statuses should skip the retry.
const MAX_RETRIES = 2;
// Exponential backoff between attempts: 300ms, then 900ms.
const RETRY_BASE_DELAY = 300;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const _assertOk = (response: Response, url: string): Response => {
  if (!response.ok) {
    throw new Error(`HTTP error for "${url}": ${response.status} ${response.statusText}`);
  }
  return response;
};

// Single fetch with a hard timeout. When AbortController is available (all
// current browser/Node targets) the timeout actually aborts the request,
// freeing the connection instead of leaking it like the old Promise.race did.
// When it is somehow absent (e.g. an unexpectedly old runtime) we can't cancel
// the request, but we still guarantee a timeout by racing a rejection so a hung
// fetch can never stall the retry loop.
const _fetchWithTimeout = async (url: string, timeout: number): Promise<Response> => {
  if (typeof AbortController !== "undefined") {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
      return _assertOk(await fetch(url, { signal: controller.signal }), url);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error(`Request for "${url}" timed out after ${timeout}ms`)),
      timeout
    );
  });
  try {
    return _assertOk(await Promise.race([fetch(url), timeoutPromise]), url);
  } finally {
    clearTimeout(timeoutId);
  }
};

// handles timeout, retries, and errors for API requests. `read` consumes the
// body inside the retry, since a connection can drop after the headers arrive.
const _makeRequest = async <T>(
  url: string,
  read: (response: Response) => T | Promise<T>,
  { timeout = DEFAULT_TIMEOUT, retries = MAX_RETRIES } = {}
): Promise<T> => {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop -- retries are intentionally sequential
      return await read(await _fetchWithTimeout(url, timeout));
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        // eslint-disable-next-line no-await-in-loop -- backoff before the next attempt
        await delay(RETRY_BASE_DELAY * 3 ** attempt);
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError));
};

/**
 * @param {String} internalName
 * @param {String} apiUrl
 * @returns {Object} parsed json manifest
 */
const fetchFtlManifest = async (
  internalName: string,
  s3ImagesBucket: string,
  apiUrl: string
): Promise<FtlManifest> => {
  let url = `${apiUrl}translations/manifest`;
  if (!getIsDev(internalName)) {
    url = `https://${s3ImagesBucket}.s3.amazonaws.com/${getTranslationsKeyPrefix(internalName)}translations/locales/manifest.json`;
  }
  const manifest = await _makeRequest(url, (response) => response.json());
  return manifest;
};

/**
 * Fetches the default `en.ftl` file for a given product
 * @param {String} product Narmi product code
 * @param {String} apiUrl
 * @returns {String} content of the ftl file
 */
const fetchDefaultFtl = async (product: string, apiUrl: string): Promise<string> => {
  const ftl = await _makeRequest(`${apiUrl}translations/${product}/default`, (response) =>
    response.text()
  );
  return ftl;
};

/**
 * Fetches a single institution-specific FTL file
 * @param {String} internalName
 * @param {String} latestFtlPath "latest" property from manifest
 * @param {String} apiUrl
 * @returns {String} content of the ftl file
 */
const fetchInstitutionFtl = async (
  internalName: string,
  s3ImagesBucket: string,
  latestFtlPath: string,
  apiUrl: string
): Promise<string> => {
  let url = `${apiUrl}translations/${latestFtlPath}`.toLowerCase();

  if (!getIsDev(internalName)) {
    url = `https://${s3ImagesBucket}.s3.amazonaws.com/${latestFtlPath}`;
  }

  const ftl = await _makeRequest(url, (response) => response.text());
  return ftl;
};

/**
 * @object api
 * These functions are stand-ins for what will eventually
 * be calls to ApiHttp endpoints.
 */
const api = { fetchFtlManifest, fetchDefaultFtl, fetchInstitutionFtl };

export default api;
